import React, { Component } from 'react';
import { Container, Row, Col } from 'reactstrap';

class GalleryDetail extends Component {
    constructor(props) {
        super(props);

        this.state = {
            gallery: null,
            item: null,
            siteSettings: null,
            footerSvgContent: '',
            footerAltText: '',
            loading: true,
            error: null,
        };
    }

    componentDidMount() {
        this.loadGalleryItem();
        this.loadSiteSettings();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.slug !== this.props.slug || prevProps.id !== this.props.id) {
            this.loadGalleryItem();
        }
    }

    loadGalleryItem = () => {
        const { slug, id } = this.props;

        if (!slug) {
            this.setState({
                gallery: null,
                item: null,
                loading: false,
                error: 'No gallery category was provided in the URL.',
            });
            return;
        }

        if (!id) {
            this.setState({
                gallery: null,
                item: null,
                loading: false,
                error: 'No gallery item id was provided in the URL.',
            });
            return;
        }

        this.setState({
            gallery: null,
            item: null,
            loading: true,
            error: null,
        });

        fetch(
            `${process.env.REACT_APP_API_URL}/api/galleries?where[slug][equals]=${encodeURIComponent(
                slug
            )}&depth=1&limit=1`
        )
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                return res.json();
            })
            .then((data) => {
                const gallery = data?.docs?.[0] || null;

                if (!gallery) {
                    this.setState({
                        gallery: null,
                        item: null,
                        loading: false,
                        error: `Gallery "${slug}" was not found.`,
                    });
                    return;
                }

                const images = gallery.images || [];
                const item =
                    images.find((img) => {
                        if (img?.id && id) return img.id === id;
                        if (img?._id && id) return img._id === id;
                        return false;
                    }) || null;

                if (!item) {
                    this.setState({
                        gallery,
                        item: null,
                        loading: false,
                        error: `Gallery item "${id}" was not found.`,
                    });
                    return;
                }

                this.setState({
                    gallery,
                    item,
                    loading: false,
                    error: null,
                });
            })
            .catch((error) => {
                console.error('Gallery detail fetch error:', error);
                this.setState({
                    gallery: null,
                    item: null,
                    loading: false,
                    error: error.message || 'Failed to load gallery item.',
                });
            });
    };

    loadSiteSettings = () => {
        fetch(`${process.env.REACT_APP_API_URL}/api/globals/site-settings?depth=1`)
            .then(async (res) => {
                if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                const data = await res.json();

                const footerLogo = data.footerLogo;
                let footerSvgContent = '';

                if (footerLogo?.url) {
                    try {
                        const svgRes = await fetch(footerLogo.url);
                        if (svgRes.ok) {
                            footerSvgContent = await svgRes.text();
                        } else {
                            console.warn('Footer SVG fetch failed:', svgRes.status);
                        }
                    } catch (err) {
                        console.error('Footer SVG fetch error:', err);
                    }
                }

                this.setState({
                    siteSettings: data,
                    footerSvgContent,
                    footerAltText: footerLogo?.altText || 'Footer Logo',
                });
            })
            .catch((error) => {
                console.error('Site settings fetch error:', error);
            });
    };

    getMediaUrl = (mediaObj) => {
        if (!mediaObj) return '';

        // Prefer the stored url (S3 or absolute) — fall back to constructing
        // from filename only if url is absent.
        if (mediaObj.url) {
            return mediaObj.url;
        }

        if (mediaObj.filename) {
            return `${process.env.REACT_APP_API_URL}/api/media/file/${mediaObj.filename}`;
        }

        return '';
    };

    render() {
        const {
            gallery,
            item,
            footerSvgContent,
            footerAltText,
            loading,
            error,
        } = this.state;

        if (loading) {
            return (
                <Container className="my-5">
                    <p className="text-center">Loading gallery item...</p>
                </Container>
            );
        }

        if (error) {
            return (
                <Container className="my-5">
                    <p className="text-center text-danger">Error: {error}</p>
                </Container>
            );
        }

        if (!gallery || !item) {
            return (
                <Container className="my-5">
                    <p className="text-center">No gallery item found.</p>
                </Container>
            );
        }

        const imageUrl = this.getMediaUrl(item.image);
        const images = gallery.images || [];

        const currentIndex = images.findIndex((img) => {
            if (item?.id && img?.id) {
                return img.id === item.id;
            }

            if (item?._id && img?._id) {
                return img._id === item._id;
            }

            return false;
        });

        const previousItem = currentIndex > 0 ? images[currentIndex - 1] : null;
        const nextItem =
            currentIndex >= 0 && currentIndex < images.length - 1
                ? images[currentIndex + 1]
                : null;

        const previousHref =
            previousItem && (previousItem.id || previousItem._id)
                ? `/gallery/${gallery.slug}/${previousItem.id || previousItem._id}`
                : null;

        const nextHref =
            nextItem && (nextItem.id || nextItem._id)
                ? `/gallery/${gallery.slug}/${nextItem.id || nextItem._id}`
                : null;

        return (
            <Container className="my-5 mb-4">
                <Row className="mb-4">
                    <Col xs={12}>
                        <div>
                            <h1>{item.title || gallery.name}</h1>

                            <p style={{ marginBottom: '0.5rem' }}>
                                <a
                                    href={`/gallery/${gallery.slug}`}
                                    style={{ textDecoration: 'none' }}
                                >
                                    Back to {gallery.name}
                                </a>
                            </p>
                        </div>
                    </Col>

                    <Col>
                        <Row style={{ padding: '0 0.8rem' }}>
                            <div className="bg-primary mt-4 mb-3" style={{ height: '1px' }} />
                        </Row>
                    </Col>
                </Row>

                <Row className="mb-5">
                    <Col lg={8} md={12} className="mb-4 mb-lg-0">
                        {imageUrl ? (
                            <div
                                style={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    backgroundColor: '#f8f9fa',
                                }}
                            >
                                <img
                                    src={imageUrl}
                                    alt={item.title || gallery.name || 'Gallery item'}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                        objectFit: 'contain',
                                    }}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    minHeight: '400px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#777',
                                    backgroundColor: '#f8f9fa',
                                }}
                            >
                                No image
                            </div>
                        )}
                    </Col>

                    <Col lg={4} md={12}>
                        <div>
                            <h3>{item.title || 'Product Details'}</h3>

                            {item.description && (
                                <p style={{ color: '#666', lineHeight: '1.6' }}>
                                    {item.description}
                                </p>
                            )}

                            <div className="mt-4">
                                {previousHref && (
                                    <a
                                        href={previousHref}
                                        style={{ marginRight: '1rem', textDecoration: 'none' }}
                                    >
                                        ← Previous
                                    </a>
                                )}

                                {nextHref && (
                                    <a
                                        href={nextHref}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        Next →
                                    </a>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {footerSvgContent && (
                    <>
                        <Row className="mt-5 mb-4">
                            <Col>
                                <div className="bg-primary" style={{ height: '1px' }} />
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col xs={12}>
                                <div style={{ textAlign: 'center' }}>
                                    <div
                                        aria-label={footerAltText}
                                        dangerouslySetInnerHTML={{ __html: footerSvgContent }}
                                        style={{
                                            maxWidth: '200px',
                                            margin: '0 auto',
                                            opacity: 0.9,
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        );
    }
}

export default GalleryDetail;